import html2pdf from "html2pdf.js";
import {
  getCurrencySymbol,
  getPrinterSafeCurrencySymbol,
  readStoredCurrencyContext,
} from "./currency";
import { writeUsbForRuntime } from "./runtimeUsbPrinter";

const sanitize = (v) => {
  if (v === undefined || v === null) return "";
  const s = String(v);
  return `"${s.replace(/"/g, '""')}"`;
};

const NO_DATA_MESSAGE = "No data available. Download was not started.";

const showNoDataMessage = () => {
  if (typeof window !== "undefined") {
    window.alert(NO_DATA_MESSAGE);
  }
};

const hasNonEmptyRows = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((row) =>
    Array.isArray(row) &&
    row.some((cell) => String(cell ?? "").trim().length > 0)
  );
};

const getRestaurantMeta = () => {
  const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
  const name =
    data.restaurant_name || data.restaurantname || data.name || "Bill Sarthi";
  const address = [
    data.addressline1 || data.address || "",
    data.addressline2 || "",
    data.addressline3 || "",
    data.city || data.restaurant_city || "",
  ]
    .filter(Boolean)
    .join(", ");
  const phone =
    data.mobile_number ||
    data.mobile ||
    data.restaurantphone ||
    data.phone ||
    data.phonenumber ||
    "";
  return {
    name,
    address,
    phone,
    generatedOn: new Date().toLocaleString("en-IN"),
  };
};

const cleanExportClone = (root) => {
  if (!root) return;

  // Remove common interactive controls from export output.
  root
    .querySelectorAll(
      "button, input, select, textarea, [role='button'], .no-print, .no-export, [data-export-ignore='true']"
    )
    .forEach((el) => el.remove());

  // Important: do not aggressively remove "empty" nodes here.
  // Visual nodes like <img>, icon wrappers, and styled containers can appear
  // text-empty but are still required for html2canvas/pdf rendering.
};

const hasExportableDomData = (root) => {
  if (!root) return false;

  // Strong positive signal: table body has real cells.
  const dataCells = root.querySelectorAll("tbody td");
  if (dataCells.length > 0) {
    const hasValue = Array.from(dataCells).some(
      (td) => String(td.textContent || "").trim().length > 0
    );
    if (hasValue) return true;
  }

  const text = String(root.textContent || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

  if (!text) return false;

  // Common empty-state messages used in reports.
  if (
    text.includes("no data") ||
    text.includes("no entries") ||
    text.includes("no records") ||
    text.includes("unable to load")
  ) {
    return false;
  }

  // Fallback signal for non-table reports (cards/charts/bars with text values).
  return text.length > 40;
};

const waitForCloneImages = async (root, timeoutMs = 5000) => {
  const images = Array.from(root?.querySelectorAll?.("img") || []);
  if (!images.length) return;

  await Promise.all(
    images.map((img) => {
      const src = String(img.getAttribute("src") || img.src || "");
      if (!src) return Promise.resolve();

      if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const probe = new window.Image();
        const done = () => resolve();
        const isRemoteHttp = /^https?:\/\//i.test(src);
        if (isRemoteHttp) {
          probe.crossOrigin = "anonymous";
          img.crossOrigin = "anonymous";
        }
        probe.onload = done;
        probe.onerror = done;
        probe.src = src;
        window.setTimeout(done, timeoutMs);
      });
    })
  );
};

export const downloadCSV = (filename, headers = [], rows = []) => {
  if (!hasNonEmptyRows(rows)) {
    showNoDataMessage();
    return false;
  }

  const meta = getRestaurantMeta();
  const prefixedRows =
    Array.isArray(headers) && headers.length
      ? [
          [`Restaurant: ${meta.name}`],
          [`Address: ${meta.address || "-"}`],
          [`Generated On: ${meta.generatedOn}`],
          [],
          headers,
          ...rows,
          [],
          ["Auto-generated with Bill Sarthi"],
        ]
      : [headers, ...rows];
  const csv = prefixedRows
    .map((row) => row.map(sanitize).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
};

export const downloadExcel = (filename, headers = [], rows = []) => {
  if (!hasNonEmptyRows(rows)) {
    showNoDataMessage();
    return false;
  }

  const meta = getRestaurantMeta();
  const cleanFilename = String(filename || "report.xlsx").replace(/[^A-Za-z0-9._-]+/g, "_");
  const finalFilename =
    cleanFilename.endsWith(".xlsx") || cleanFilename.endsWith(".xls")
      ? cleanFilename
      : `${cleanFilename.replace(/\.(csv|pdf)$/i, "")}.xlsx`;

  const escapeXml = (v) => {
    if (v === undefined || v === null) return "";
    return String(v)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
  };

  const colCount = Math.max(
    headers?.length || 0,
    ...(rows || []).map((r) => (Array.isArray(r) ? r.length : 1)),
    1
  );

  let xml = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta http-equiv="content-type" content="text/html; charset=UTF-8"/>
<!--[if gte mso 9]>
<xml>
 <x:ExcelWorkbook>
  <x:ExcelWorksheets>
   <x:ExcelWorksheet>
    <x:Name>Report</x:Name>
    <x:WorksheetOptions>
     <x:DisplayGridlines/>
    </x:WorksheetOptions>
   </x:ExcelWorksheet>
  </x:ExcelWorksheets>
 </x:ExcelWorkbook>
</xml>
<![endif]-->
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; }
  table { border-collapse: collapse; width: 100%; margin-top: 8px; }
  .title { font-size: 16px; font-weight: bold; color: #015b9a; background-color: #f0f7ff; height: 32px; text-align: left; padding: 6px; }
  .meta { font-size: 11px; color: #475569; background-color: #f8fafc; height: 22px; padding: 4px; }
  th { background-color: #015b9a; color: #ffffff; font-weight: bold; border: 1px solid #cbd5e1; text-align: left; padding: 8px; font-size: 12px; }
  td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; font-size: 12px; color: #1e293b; }
  tr:nth-child(even) td { background-color: #f8fafc; }
  .footer { font-size: 11px; color: #64748b; font-style: italic; text-align: center; height: 25px; }
</style>
</head>
<body>
<table>
  <tr><td colspan="${colCount}" class="title">${escapeXml(meta.name)}</td></tr>
  <tr><td colspan="${colCount}" class="meta">Address: ${escapeXml(meta.address || "-")}${meta.phone ? ` | Phone: ${escapeXml(meta.phone)}` : ""}</td></tr>
  <tr><td colspan="${colCount}" class="meta">Generated On: ${escapeXml(meta.generatedOn)}</td></tr>
  <tr><td colspan="${colCount}"></td></tr>
`;

  if (Array.isArray(headers) && headers.length) {
    xml += "  <tr>\n";
    headers.forEach((h) => {
      xml += `    <th>${escapeXml(h)}</th>\n`;
    });
    xml += "  </tr>\n";
  }

  (rows || []).forEach((row) => {
    xml += "  <tr>\n";
    const cells = Array.isArray(row) ? row : [row];
    cells.forEach((cell) => {
      xml += `    <td>${escapeXml(cell)}</td>\n`;
    });
    xml += "  </tr>\n";
  });

  xml += `
  <tr><td colspan="${colCount}"></td></tr>
  <tr><td colspan="${colCount}" class="footer">Auto-generated with Bill Sarthi</td></tr>
</table>
</body>
</html>`;

  const blob = new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", finalFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
};


export const downloadPDF = async (element, filename) => {
  if (!element) return;
  const meta = getRestaurantMeta();
  const contentClone = element.cloneNode(true);
  cleanExportClone(contentClone);
  if (!hasExportableDomData(contentClone)) {
    showNoDataMessage();
    return false;
  }

  const root = document.createElement("div");
  root.style.background = "#ffffff";
  root.style.padding = "0";
  root.style.color = "#111827";
  root.style.fontFamily = "Arial, Helvetica, sans-serif";

  const header = document.createElement("div");
  header.style.background = "linear-gradient(135deg, #015b9a, #003962)";
  header.style.color = "#ffffff";
  header.style.padding = "10px 12px";
  header.style.borderRadius = "10px";
  header.style.marginBottom = "10px";
  header.innerHTML = `
    <div style="font-size:18px;font-weight:800;">${meta.name}</div>
    <div style="font-size:12px;opacity:0.95;margin-top:2px;">${meta.address || "-"}</div>
    <div style="font-size:12px;opacity:0.95;margin-top:2px;">${meta.phone ? `Phone: ${meta.phone}` : ""}</div>
    <div style="font-size:11px;opacity:0.85;margin-top:6px;">Generated On: ${meta.generatedOn}</div>
  `;

  const bodyWrap = document.createElement("div");
  bodyWrap.className = "bs-export-body";
  bodyWrap.style.border = "1px solid #e5e7eb";
  bodyWrap.style.borderRadius = "10px";
  bodyWrap.style.padding = "10px";
  bodyWrap.style.background = "#ffffff";
  bodyWrap.appendChild(contentClone);

  const footer = document.createElement("div");
  footer.style.marginTop = "10px";
  footer.style.paddingTop = "8px";
  footer.style.borderTop = "1px solid #e5e7eb";
  footer.style.fontSize = "12px";
  footer.style.color = "#475569";
  footer.style.textAlign = "center";
  footer.textContent = "Auto-generated with Bill Sarthi";

  root.appendChild(header);
  root.appendChild(bodyWrap);
  root.appendChild(footer);

  await waitForCloneImages(root);

  const opt = {
    margin: 10,
    filename,
    html2canvas: {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: "#ffffff",
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  };
  await html2pdf().set(opt).from(root).save();
  return true;
};

const normalizePrinterWidth = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "a4" || raw === "a4-size" || raw === "a4size") {
    return "a4";
  }
  if (
    raw === "4" ||
    raw === "4in" ||
    raw === "4inch" ||
    raw === "4-inch" ||
    raw === "4 in" ||
    raw === "112mm" ||
    raw === "104mm" ||
    raw === "102mm" ||
    raw === "100mm"
  ) {
    return "4in";
  }
  if (
    raw === "3" ||
    raw === "3in" ||
    raw === "3inch" ||
    raw === "3-inch" ||
    raw === "80mm" ||
    raw === "76mm" ||
    raw === "72mm" ||
    raw === "80"
  ) {
    return "3in";
  }
  return "2in";
};

const getThermalPaper = () => {
  const saved =
    localStorage.getItem("printerWidth") || localStorage.getItem("printer_width");
  const width = normalizePrinterWidth(saved);
  if (width === "a4") {
    return { key: "a4", mm: null, px: 1100, fontSize: 13, size: "A4", margin: "10mm" };
  }
  if (width === "4in") {
    return { key: "4in", mm: 104, px: 740, fontSize: 12, size: "104mm auto", margin: "4mm" };
  }
  return width === "3in"
    ? { key: "3in", mm: 80, px: 576, fontSize: 12, size: "80mm auto", margin: "4mm" }
    : { key: "2in", mm: 58, px: 420, fontSize: 11, size: "58mm auto", margin: "4mm" };
};

const ESC = 0x1b;
const GS = 0x1d;
const INIT_CMD = new Uint8Array([ESC, 0x40]);
const CUT_CMD = new Uint8Array([GS, 0x56, 0x00]);
const ALIGN_LEFT = new Uint8Array([ESC, 0x61, 0x00]);

const BLE_OPTIONAL_SERVICES = [
  0xffe0,
  0xffe1,
  0xffe5,
  0x18f0,
  0x18f1,
  0xff00,
  0xff01,
  0xff02,
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb",
  "000018f1-0000-1000-8000-00805f9b34fb",
];

const BLE_KNOWN_SERVICES = [
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb",
  "000018f1-0000-1000-8000-00805f9b34fb",
];

const BLE_KNOWN_CHARS = [
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
];

const BLE_STATE = {
  characteristic: null,
  deviceId: "",
};

const chunkBytes = (bytes, size = 20) => {
  const out = [];
  for (let i = 0; i < bytes.length; i += size) out.push(bytes.slice(i, i + size));
  return out;
};

const centerLine = (text = "", width = 32) => {
  const s = String(text || "").trim();
  if (!s) return "";
  if (s.length >= width) return s.slice(0, width);
  const gap = width - s.length;
  const left = Math.floor(gap / 2);
  return " ".repeat(left) + s + " ".repeat(gap - left);
};

const padR = (txt, w) => {
  const s = String(txt ?? "");
  return (s + " ".repeat(Math.max(0, w - s.length))).slice(0, w);
};

const padL = (txt, w) => {
  const s = String(txt ?? "");
  return (" ".repeat(Math.max(0, w - s.length)) + s).slice(-w);
};

const wrapText = (text, width) => {
  const s = String(text || "");
  if (!s.trim()) return [""];
  const out = [];
  let rem = s.trim();
  while (rem.length) {
    if (rem.length <= width) {
      out.push(rem);
      break;
    }
    const slice = rem.slice(0, width + 1);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0) {
      out.push(slice.slice(0, lastSpace));
      rem = rem.slice(lastSpace + 1);
    } else {
      out.push(rem.slice(0, width));
      rem = rem.slice(width);
    }
    rem = rem.trimStart();
  }
  return out.length ? out : [""];
};

const findWritableCharacteristic = async (server, device) => {
  const services = await server.getPrimaryServices();
  for (const svc of services) {
    const chars = await svc.getCharacteristics();
    for (const c of chars) {
      const { write, writeWithoutResponse } = c.properties || {};
      if (write || writeWithoutResponse) {
        BLE_STATE.characteristic = c;
        BLE_STATE.deviceId = device?.id || "";
        if (device?.id) localStorage.setItem("ble_last_device_id", device.id);
        return c;
      }
    }
  }
  return null;
};

const findWritableCharacteristicFallback = async (server, device) => {
  for (const sid of BLE_KNOWN_SERVICES) {
    try {
      const svc = await server.getPrimaryService(sid);
      const chars = await svc.getCharacteristics();
      for (const c of chars) {
        const { write, writeWithoutResponse } = c.properties || {};
        if (write || writeWithoutResponse) {
          BLE_STATE.characteristic = c;
          BLE_STATE.deviceId = device?.id || "";
          if (device?.id) localStorage.setItem("ble_last_device_id", device.id);
          return c;
        }
      }
      for (const cid of BLE_KNOWN_CHARS) {
        try {
          const c = await svc.getCharacteristic(cid);
          const { write, writeWithoutResponse } = c.properties || {};
          if (write || writeWithoutResponse) {
            BLE_STATE.characteristic = c;
            BLE_STATE.deviceId = device?.id || "";
            if (device?.id) localStorage.setItem("ble_last_device_id", device.id);
            return c;
          }
        } catch {
          // continue
        }
      }
    } catch {
      // continue
    }
  }
  return null;
};

const connectBlePrinter = async () => {
  if (!navigator?.bluetooth) throw new Error("Web Bluetooth not supported");

  if (BLE_STATE.characteristic) return BLE_STATE.characteristic;

  if (navigator.bluetooth.getDevices) {
    try {
      const devices = await navigator.bluetooth.getDevices();
      const preferredId = localStorage.getItem("ble_last_device_id") || BLE_STATE.deviceId;
      const device = preferredId
        ? devices.find((d) => d.id === preferredId)
        : devices[0];
      if (device) {
        const server = device.gatt?.connected ? device.gatt : await device.gatt.connect();
        let c = await findWritableCharacteristic(server, device);
        if (!c) c = await findWritableCharacteristicFallback(server, device);
        if (c) return c;
      }
    } catch {
      // proceed to manual picker
    }
  }

  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: BLE_OPTIONAL_SERVICES,
  });
  const server = await device.gatt.connect();
  let c = await findWritableCharacteristic(server, device);
  if (!c) c = await findWritableCharacteristicFallback(server, device);
  if (!c) throw new Error("No writable Bluetooth characteristic found");
  return c;
};

const writeBle = async (bytes) => {
  const c = BLE_STATE.characteristic || (await connectBlePrinter());
  const parts = chunkBytes(bytes, 20);
  const canWriteWithResponse =
    !!c.writeValue && c?.properties?.write !== false;
  const canWriteWithoutResponse = !!c.writeValueWithoutResponse;

  console.info("[Thermal Report] Sending complete payload", {
    byteLength: bytes?.length || 0,
    packetCount: parts.length,
    writeWithResponse: canWriteWithResponse,
    writeWithoutResponse: canWriteWithoutResponse,
  });

  for (let index = 0; index < parts.length; index += 1) {
    if (canWriteWithResponse) {
      await c.writeValue(parts[index]);
      await new Promise((resolve) => setTimeout(resolve, 2));
    } else if (canWriteWithoutResponse) {
      await c.writeValueWithoutResponse(parts[index]);
      // BLE printers usually have a very small receive buffer. Pacing prevents
      // the tail of longer reports from being silently dropped.
      await new Promise((resolve) => setTimeout(resolve, 5));
      if ((index + 1) % 20 === 0) {
        await new Promise((resolve) => setTimeout(resolve, 30));
      }
    } else {
      throw new Error("Selected printer characteristic is not writable");
    }
  }

  // Let the printer drain its buffer before processing the final cut command.
  await new Promise((resolve) => setTimeout(resolve, 120));
};

const buildRowLines = (cells, widths, numericColumns = []) => {
  const wrapped = cells.map((cell, index) => wrapText(cell, widths[index]));
  const maxLines = wrapped.reduce((m, arr) => Math.max(m, arr.length), 1);
  const out = [];
  for (let i = 0; i < maxLines; i += 1) {
    const line = wrapped.map((arr, index) => {
      const value = arr[i] || "";
      return numericColumns[index] && i === 0
        ? padL(value, widths[index])
        : padR(value, widths[index]);
    }).join(" ");
    out.push(line);
  }
  return out;
};

const humanizeReportTitle = (title = "") => {
  const withoutExtension = String(title || "")
    .replace(/\.(pdf|csv|xlsx?)$/i, "")
    .replace(/_?\d{4}-\d{2}-\d{2}(?:_to_\d{4}-\d{2}-\d{2})?.*$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return withoutExtension
    .split(" ")
    .map((word) => word ? `${word[0].toUpperCase()}${word.slice(1)}` : "")
    .join(" ") || "Report";
};

const receiptPeriodFromTitle = (title = "") => {
  const dates = String(title || "").match(/\d{4}-\d{2}-\d{2}/g) || [];
  const display = (date) => {
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };
  if (dates.length >= 2) return `From ${display(dates[0])} to ${display(dates[1])}`;
  if (dates.length === 1) return `Date: ${display(dates[0])}`;
  return "";
};

const numericLooking = (value = "") => {
  const clean = String(value).replace(/[₹$,Rs.NPR%()\s-]/gi, "");
  return clean !== "" && /^\d+(?:\.\d+)?$/.test(clean);
};

const directCells = (row, selector) =>
  Array.from(row?.children || [])
    .filter((node) => node.matches?.(selector))
    .map((node) => (node.textContent || "").replace(/\s+/g, " ").trim());

const nearestTableHeading = (table, root) => {
  const headings = Array.from(root?.querySelectorAll?.("h1,h2,h3,h4") || []);
  const previous = headings.filter(
    (heading) => heading.compareDocumentPosition(table) & 4
  );
  return (previous[previous.length - 1]?.textContent || "").trim();
};

export const buildReportReceiptText = (element, title, lineWidth) => {
  const lines = [];
  const meta = getRestaurantMeta();
  const userData = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
  const heading = Array.from(element?.querySelectorAll?.("h1,h2") || [])
    .map((node) => (node.textContent || "").trim())
    .find((text) => /report|summary|register/i.test(text));
  const headerTitle = heading || humanizeReportTitle(title);
  const periodLabel = receiptPeriodFromTitle(title);
  const separator = "-".repeat(lineWidth);
  const strongSeparator = "=".repeat(lineWidth);
  const pushCentered = (value) => wrapText(value, lineWidth)
    .forEach((part) => lines.push(centerLine(part, lineWidth)));

  pushCentered(meta.name);
  if (meta.address) pushCentered(meta.address);
  if (meta.phone) pushCentered(`Phone: ${meta.phone}`);
  const registration =
    userData.fssai_number || userData.fssai_no || userData.fssai ||
    userData.gstin || userData.gst_number || userData.gstno || "";
  if (registration) pushCentered(`FSSAI/GST: ${registration}`);
  lines.push("");
  pushCentered(headerTitle);
  if (periodLabel) pushCentered(periodLabel);
  pushCentered(`Printed at: ${new Date().toLocaleString("en-IN")}`);
  lines.push(separator);

  const tables = Array.from(element?.querySelectorAll?.("table") || []);
  if (!tables.length) {
    const raw = (element?.innerText || "")
      .split("\n")
      .map((t) => t.trim())
      .filter(Boolean)
      .filter(
        (t) =>
          !/^(refresh|pdf|csv|thermal print|thermal print via usb)$/i.test(t) &&
          !/^from:?$/i.test(t) &&
          !/^to:?$/i.test(t)
      );
    raw.forEach((r) => wrapText(r, lineWidth).forEach((w) => lines.push(w)));
    lines.push(strongSeparator);
    return lines.join("\n");
  }

  tables.forEach((table, index) => {
    if (index > 0) lines.push(separator);
    const sectionHeading = nearestTableHeading(table, element);
    if (sectionHeading && sectionHeading !== headerTitle) {
      pushCentered(sectionHeading);
      lines.push(separator);
    }
    const headerRow = table.querySelector("thead tr");
    const th = directCells(headerRow, "th,td");
    const bodyRows = Array.from(table.querySelectorAll("tbody tr, tfoot tr"))
      .map((tr) => directCells(tr, "td,th"))
      .filter((row) => row.length && row.some(Boolean));
    const colCount = Math.max(th.length, ...bodyRows.map((row) => row.length), 0);
    if (!colCount) return;

    if (colCount <= 3) {
      const gapCount = colCount - 1;
      const available = lineWidth - gapCount;
      const widths = colCount === 1
        ? [available]
        : colCount === 2
          ? [Math.ceil(available * 0.62), Math.floor(available * 0.38)]
          : [available - Math.max(5, Math.floor(available * 0.18)) - Math.max(9, Math.floor(available * 0.29)), Math.max(5, Math.floor(available * 0.18)), Math.max(9, Math.floor(available * 0.29))];
      const numericColumns = Array.from({ length: colCount }, (_, column) =>
        bodyRows.filter((row) => row[column]).every((row) => numericLooking(row[column]))
      );
      if (th.length) {
        buildRowLines(th.slice(0, colCount), widths).forEach((line) => lines.push(line));
        lines.push(separator);
      }
      bodyRows.forEach((row) => {
        const safe = Array.from({ length: colCount }, (_, column) => row[column] || "");
        buildRowLines(safe, widths, numericColumns).forEach((line) => lines.push(line));
      });
    } else {
      bodyRows.forEach((row, rowIndex) => {
        if (rowIndex > 0) lines.push(separator);
        row.forEach((value, column) => {
          if (!value) return;
          const label = th[column] || `Field ${column + 1}`;
          const prefix = `${label}: `;
          if (prefix.length > Math.floor(lineWidth * 0.45)) {
            wrapText(`${label}:`, lineWidth).forEach((part) => lines.push(part));
            wrapText(value, lineWidth - 2).forEach((part) => lines.push(`  ${part}`));
            return;
          }
          const valueWidth = lineWidth - prefix.length;
          const valueLines = wrapText(value, valueWidth);
          lines.push(`${prefix}${valueLines[0]}`);
          valueLines.slice(1).forEach((part) => lines.push(`${" ".repeat(prefix.length)}${part}`));
        });
      });
    }
  });

  lines.push(strongSeparator);
  return lines.join("\n");
};

const normalizeThermalReceiptText = (value) => {
  const context = readStoredCurrencyContext();
  const displaySymbol = getCurrencySymbol(context);
  const printerSymbol = getPrinterSafeCurrencySymbol(context);
  let text = String(value || "");

  if (displaySymbol && printerSymbol && displaySymbol !== printerSymbol) {
    text = text.split(displaySymbol).join(printerSymbol);
  }

  // Reports may contain a symbol saved before the restaurant's current
  // regional setting. Keep common non-ASCII currency symbols printer-safe.
  return text
    .split("₹").join("Rs.")
    .split("रु").join("NPR")
    .split("€").join("EUR")
    .split("£").join("GBP")
    .split("¥").join("JPY");
};

const fallbackBrowserThermalText = (text, title = "Thermal Report") => {
  const paper = getThermalPaper();
  const popup = window.open("", "_blank", `width=${paper.px},height=900`);
  if (!popup) return;
  const safeText = String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  popup.document.open();
  popup.document.write(`
    <!doctype html><html><head><meta charset="utf-8" /><title>${title}</title>
    <style>
      @page { size: ${paper.size || `${paper.mm}mm auto`}; margin: ${paper.margin || "2mm"}; }
      html, body { margin: 0; padding: 0; background: #fff; color: #000; }
      pre { margin: 0; white-space: pre; font-family: "Courier New", monospace; font-size: ${paper.fontSize}px; line-height: 1.25; }
    </style></head><body><pre>${safeText}</pre>
    <script>window.onload=function(){setTimeout(function(){window.focus();window.print();},120);};</script>
    </body></html>
  `);
  popup.document.close();
};

export const printThermal = async (element, title = "Thermal Report", options = {}) => {
  if (!element) return;
  const paper = getThermalPaper();
  const lineWidth =
    paper.key === "a4" ? 72 : paper.key === "4in" ? 64 : paper.key === "3in" ? 48 : 32;
  const receiptText = normalizeThermalReceiptText(
    typeof options?.buildReceiptText === "function"
      ? options.buildReceiptText(lineWidth)
      : buildReportReceiptText(element, title, lineWidth)
  );
  const enc = new TextEncoder();
  const chunks = [
    INIT_CMD,
    ALIGN_LEFT,
    enc.encode(receiptText + "\n\n\n"),
  ];
  const totalLen = chunks.reduce((sum, arr) => sum + arr.length, 0);
  const payload = new Uint8Array(totalLen);
  let offset = 0;
  chunks.forEach((arr) => {
    payload.set(arr, offset);
    offset += arr.length;
  });

  try {
    await writeBle(payload);
    await new Promise((resolve) => setTimeout(resolve, 180));
    await writeBle(CUT_CMD);
  } catch (error) {
    fallbackBrowserThermalText(receiptText, title);
  }
};

/**
 * Prints a report directly to a USB thermal printer using ESC/POS bytes.
 * In Electron this uses the native printer bridge; in supported browsers it
 * uses WebUSB and prompts the user to select a printer when needed.
 */
export const printThermalViaUsb = async (element, title = "Thermal Report", options = {}) => {
  if (!element) return false;
  const paper = getThermalPaper();
  const lineWidth =
    paper.key === "a4" ? 72 : paper.key === "4in" ? 64 : paper.key === "3in" ? 48 : 32;
  const receiptText = normalizeThermalReceiptText(
    typeof options?.buildReceiptText === "function"
      ? options.buildReceiptText(lineWidth)
      : buildReportReceiptText(element, title, lineWidth)
  );
  const enc = new TextEncoder();
  const textBytes = enc.encode(receiptText + "\n\n\n");
  const payload = new Uint8Array(
    INIT_CMD.length + ALIGN_LEFT.length + textBytes.length + CUT_CMD.length
  );
  let offset = 0;
  [INIT_CMD, ALIGN_LEFT, textBytes, CUT_CMD].forEach((chunk) => {
    payload.set(chunk, offset);
    offset += chunk.length;
  });

  try {
    await writeUsbForRuntime(payload, { allowPrompt: true });
    return true;
  } catch (error) {
    const message = error?.message || "USB thermal print failed. Please reconnect the printer.";
    if (typeof window !== "undefined") window.alert(message);
    return false;
  }
};
